import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { forwardRef } from "react";
import { cva } from "class-variance-authority";

// ============================================
// 타입 정의
// ============================================

export interface _ChipTextProps {
  type: "solid" | "outlined";
  size: "18px" | "20px" | "22px" | "24px";
  rounded: boolean;
  leftIcon?: React.ReactNode;
  leftIconClassName?: string;
  showRightIcon: boolean;
  rightIconClassName?: string;
}

// ============================================
// 스타일 (CVA)
// ============================================

const chipTextContainerVariants = cva(
  "box-border flex flex-row py-0.5 px-1 gap-0.5",
  {
    variants: {
      size: {
        "18px": "h-[18px]",
        "20px": "h-5",
        "22px": "h-[22px]",
        "24px": "h-6",
      },
      rounded: {
        true: "rounded-full",
        false: "rounded-[4px]",
      },
      type: {
        outlined: "border",
        solid: "",
      },
    },
    defaultVariants: {
      size: "20px",
      rounded: false,
    },
  },
);

const chipTextVariants = cva("flex items-center justify-center font-medium", {
  variants: {
    size: {
      "18px": "caption-2-medium",
      "20px": "caption-1-medium",
      "22px": "label-2-medium",
      "24px": "label-1-normal-medium",
    },
  },
  defaultVariants: {
    size: "20px",
  },
});

const chipLeftIconVariants = cva(
  "box-border flex h-full flex-col items-center justify-center",
  {
    variants: {
      size: {
        "18px": "w-[10px]",
        "20px": "w-[12px]",
        "22px": "w-[14px]",
        "24px": "w-[16px]",
      },
    },
  },
);

const chipRightIconVariants = cva(
  "box-border flex h-full items-center justify-center",
  {
    variants: {
      size: {
        "18px": "w-[8px]",
        "20px": "w-[10px]",
        "22px": "w-[12px]",
        "24px": "w-[14px]",
      },
    },
  },
);

// ============================================
// 컴포넌트
// ============================================

const _ChipText = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & _ChipTextProps
>(
  (
    {
      type,
      size,
      rounded,
      leftIcon,
      leftIconClassName,
      showRightIcon,
      rightIconClassName,
      className,
      children,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          chipTextContainerVariants({ size, rounded, type }),
          className,
        )}
      >
        {leftIcon && (
          <div
            className={cn(chipLeftIconVariants({ size }), leftIconClassName)}
          >
            {leftIcon}
          </div>
        )}
        <div className={cn(chipTextVariants({ size }))}>{children}</div>
        {showRightIcon && (
          <div
            className={cn(chipRightIconVariants({ size }), rightIconClassName)}
          >
            <X className="size-full" />
          </div>
        )}
      </div>
    );
  },
);

_ChipText.displayName = "_ChipText";

export default _ChipText;
